import cx from "classnames";
import { IonContent, IonLabel, IonPage } from "@ionic/react";
import { Suspense, lazy, useEffect, useMemo } from "react";
import { shallow } from "zustand/shallow";
import s from "./CancelSubscription.module.scss";
import { useUserCancelSubscriptionProvider } from "./provider";
import brandLogo from "../../../Common/assets/logo/wl-woodmark-logo-rev.svg";
import { resolveWebCancelFlowVersion } from "./getWebCancelFlowVersion";
import { WLLoader } from "../../../Common/components/WLLoader/WLLoader";
import { useWLLoaderStore } from "../../../Common/components/WLLoader/store";
import { WLResponseDialog } from "../../components/WLResponseDialog/WLResponseDialog";
import WLDialog from "../../components/WLDialog/WLDialog";
import useExistingStripeSubscriptionProvider from "../../components/WLStripePaymentMethods/setupProviders/useExistingStripeSubscriptionProvider";
import { useWebUserStore } from "../../CommonStores/user/users";
import { signOut } from "../../services/user";
import { useUserCancelSubscriptionStore } from "./store";
import { CancelSubscriptionSkeleton } from "./CancelSubscriptionSkeleton";

const StripeCancelModalV3 = lazy(() =>
  import("./CancelFlow/v3/StripeCancelModal/StripeCancelModal").then((m) => ({
    default: m.StripeCancelModal,
  }))
);
const StripeCancelModalV4 = lazy(() =>
  import("./CancelFlow/v4/StripeCancelModal/StripeCancelModal").then((m) => ({
    default: m.StripeCancelModal,
  }))
);

export const CancelSubscription = () => {
  useExistingStripeSubscriptionProvider();

  const { isLoading } = useWLLoaderStore();

  const { isLoadingProviders, inValidUserRequest } =
    useUserCancelSubscriptionProvider();
  const [hasCancelled] = useUserCancelSubscriptionStore(
    (store) => [store.hasCancelled],
    shallow
  );

  const user = useWebUserStore((state) => state.user);
  const cancelFlowVersion = useMemo(
    () => resolveWebCancelFlowVersion(user ?? undefined),
    [user],
  );

  const loadersBlocking = isLoadingProviders || isLoading;
  const showInvalid =
    !loadersBlocking && inValidUserRequest;
  const showCancelFlow =
    !loadersBlocking && !inValidUserRequest && user != null;

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      signOut();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  return (
    <IonPage className={s.page}>
      <IonContent className={cx(s.pageContent)} scrollY={true} fullscreen>
        <div className={s.body}>
          <img className={s.logo} src={brandLogo} alt="" loading="eager" decoding="async" />
          {hasCancelled ? (
            <div className={cx(s.cancelled)}>
              <IonLabel className={cx("win-body gillroy medium")}>
                Subscription cancelled successfully
              </IonLabel>
            </div>
          ) : (
            <>
              {showInvalid ? (
                <div className={cx(s.invalid)}>
                  <IonLabel>
                    Invalid cancellation link, please{" "}
                    <a
                      className={s.contactLink}
                      href="https://winnerslocker.com.au/contact/"
                      target="_newTab"
                    >
                      contact support
                    </a>
                  </IonLabel>
                </div>
              ) : showCancelFlow ? (
                <Suspense fallback={<CancelSubscriptionSkeleton />}>
                  {cancelFlowVersion === "v4" ? (
                    <StripeCancelModalV4 />
                  ) : (
                    <StripeCancelModalV3 />
                  )}
                </Suspense>
              ) : (
                <CancelSubscriptionSkeleton />
              )}
            </>
          )}
        </div>
      </IonContent>
      <WLLoader />
      <WLDialog />
      <WLResponseDialog />
    </IonPage>
  );
};
